import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '@/components/LanguageToggle';
import SEO from '@/components/SEO';

const { FiArrowLeft, FiArrowRight } = FiIcons;

export default function Journal() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  // Enhanced fallback content including featured article metadata
  const fallback = {
    en: {
      title: "The Selection Process",
      subtitle: "Inside Barberà's Elite Directory",
      intro: "Our journey through Barberà del Vallès isn't just about finding a place to sit. It's an obsession with the culinary soul of the Vallès Occidental. We've spent months navigating the streets near Barcelona to curate a list that accepts nothing but perfection.",
      foodTitle: "The Golden Tapas Standard",
      foodContent: "To us, 'Las Mejores Tapas' isn't a marketing slogan; it's a structural requirement. We look for the translucency of hand-carved Ibérico ham, the precise crunch-to-fluff ratio of a patatas brava, and the soul of a kitchen that understands the heritage of Spanish regional ingredients.",
      drinkTitle: "Craft Beer Excellence",
      drinkContent: "The rise of 'Cerveza Artesana' in the region has been meteoric. We only partner with establishments that respect the cold chain, maintain their lines with surgical precision, and prioritize local micro-breweries that define the unique taste of our valley.",
      vibeTitle: "The Art of the Pour",
      vibeContent: "Atmosphere is the silent ingredient. Whether it's the specific density of ice in a cocktail or the two-part settle of a perfect pint, we evaluate the rhythm of the bar. If it doesn't feel like an escape from the ordinary, it simply doesn't make the list.",
      featuredTag: "Featured Review",
      featuredTitle: "Inside La Barberenca: The Heart of Craft Beer",
      featuredSubtitle: "Brewpub Review",
      backBtn: "Back to Directory"
    },
    es: {
      title: "El Proceso de Selección",
      subtitle: "Dentro del Directorio de Élite de Barberà",
      intro: "Nuestro viaje por Barberà del Vallès no se trata solo de encontrar un lugar donde sentarse. Es una obsesión con el alma culinaria del Vallès Occidental. Hemos pasado meses recorriendo las calles cerca de Barcelona para curar una lista que no acepta nada menos que la perfección.",
      foodTitle: "El Estándar de Oro de las Tapas",
      foodContent: "Para nosotros, 'Las Mejores Tapas' no es un eslogan publicitario; es un requisito estructural. Buscamos la transparencia del jamón ibérico cortado a mano, la proporción precisa entre crujiente y esponjoso de una patata brava, y el alma de una cocina que entiende la herencia de los ingredientes regionales españoles.",
      drinkTitle: "Excelencia en Cerveza Artesana",
      drinkContent: "El auge de la 'Cerveza Artesana' en la región ha sido meteórico. Solo colaboramos con establecimientos que respetan la cadena de frío, mantienen sus grifos con precisión quirúrgica y priorizan las microcervecerías locales que definen el sabor único de nuestro valle.",
      vibeTitle: "El Arte de la Mezcla",
      vibeContent: "La atmósfera es el ingrediente silencioso. Ya sea la densidad específica del hielo en un cóctel o el reposo en dos tiempos de una pinta perfecta, evaluamos el ritmo del bar. Si no se siente como una escapada de lo ordinario, simplemente no entra en la lista.",
      featuredTag: "Reseña Destacada",
      featuredTitle: "Dentro de La Barberenca: El Corazón de la Cerveza Artesana",
      featuredSubtitle: "Reseña de Brewpub",
      backBtn: "Volver al Directorio"
    }
  };

  const content = lang === 'es' ? fallback.es : fallback.en;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": content.title,
    "description": content.intro,
    "author": {
      "@type": "Organization",
      "name": "The Selection Barberà"
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Selection",
      "logo": {
        "@type": "ImageObject",
        "url": "https://barberadelvalles.bar/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://barberadelvalles.bar/journal"
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-50px" },
    transition: { duration: 0.8, ease: [0.21, 0.45, 0.32, 0.9] }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-400 selection:bg-zinc-100 selection:text-zinc-950 pb-32">
      <SEO 
        title={`${content.title} | Barberà del Vallès Bar Directory`} 
        description={content.intro.substring(0, 160)} 
        schema={articleSchema}
      />
      <LanguageToggle />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-zinc-900/50 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center">
          <button 
            onClick={() => navigate('/')}
            className="group flex items-center gap-3 text-zinc-500 hover:text-zinc-50 transition-all duration-500"
          >
            <div className="w-8 h-8 rounded-full border border-zinc-800 flex items-center justify-center group-hover:border-zinc-500 transition-colors">
              <SafeIcon icon={FiArrowLeft} className="text-sm group-hover:-translate-x-0.5 transition-transform" />
            </div>
            <span className="text-[10px] font-bold tracking-[0.3em] uppercase">{content.backBtn}</span>
          </button>
        </div>
      </header>

      <main className="pt-40 px-6">
        <article className="max-w-2xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="mb-24 text-center"
          >
            <span className="text-[10px] font-bold tracking-[0.5em] text-zinc-600 uppercase block mb-6">
              {content.subtitle}
            </span>
            <h1 className="text-5xl md:text-7xl font-bold text-zinc-50 tracking-tighter mb-12 leading-[1.1]">
              {content.title}
            </h1>
            <div className="w-12 h-px bg-zinc-800 mx-auto" />
          </motion.div>

          {/* Intro Paragraph */}
          <motion.p 
            {...fadeInUp}
            className="text-xl md:text-2xl text-zinc-300 font-light leading-relaxed mb-20 italic border-l-2 border-zinc-800 pl-8"
          >
            {content.intro}
          </motion.p>

          <div className="space-y-24">
            {/* Section 1 - Food */}
            <motion.section {...fadeInUp} className="group">
              <h2 className="text-zinc-50 text-2xl font-bold mb-6 tracking-tight flex items-center gap-4">
                <span className="text-zinc-800 font-mono text-sm">01</span>
                {content.foodTitle}
              </h2>
              <div className="h-px w-full bg-zinc-900 mb-8 overflow-hidden">
                <motion.div 
                  initial={{ x: "-100%" }}
                  whileInView={{ x: "0%" }}
                  transition={{ duration: 1.5, delay: 0.2 }}
                  className="h-full w-full bg-gradient-to-r from-transparent via-zinc-700 to-transparent"
                />
              </div>
              <p className="leading-relaxed text-lg font-light text-zinc-400">
                {content.foodContent}
              </p>
            </motion.section>

            {/* Section 2 - Drinks */}
            <motion.section {...fadeInUp} className="group">
              <h2 className="text-zinc-50 text-2xl font-bold mb-6 tracking-tight flex items-center gap-4">
                <span className="text-zinc-800 font-mono text-sm">02</span>
                {content.drinkTitle}
              </h2>
              <div className="h-px w-full bg-zinc-900 mb-8 overflow-hidden">
                <motion.div 
                  initial={{ x: "-100%" }}
                  whileInView={{ x: "0%" }}
                  transition={{ duration: 1.5, delay: 0.2 }}
                  className="h-full w-full bg-gradient-to-r from-transparent via-zinc-700 to-transparent"
                />
              </div>
              <p className="leading-relaxed text-lg font-light text-zinc-400">
                {content.drinkContent}
              </p>
            </motion.section>

            {/* Section 3 - Atmosphere */}
            <motion.section {...fadeInUp} className="group">
              <h2 className="text-zinc-50 text-2xl font-bold mb-6 tracking-tight flex items-center gap-4">
                <span className="text-zinc-800 font-mono text-sm">03</span>
                {content.vibeTitle}
              </h2>
              <div className="h-px w-full bg-zinc-900 mb-8 overflow-hidden">
                <motion.div 
                  initial={{ x: "-100%" }}
                  whileInView={{ x: "0%" }}
                  transition={{ duration: 1.5, delay: 0.2 }}
                  className="h-full w-full bg-gradient-to-r from-transparent via-zinc-700 to-transparent"
                />
              </div>
              <p className="leading-relaxed text-lg font-light text-zinc-400">
                {content.vibeContent}
              </p>
            </motion.section>

            {/* Featured Article Hub Connection */}
            <motion.div 
              {...fadeInUp}
              onClick={() => navigate('/journal/la-barberenca')}
              className="group relative h-72 rounded-3xl overflow-hidden border border-zinc-800/50 cursor-pointer"
            >
              <div className="absolute inset-0 z-0">
                <img 
                  src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1779688358482-blob" 
                  alt="La Barberenca Featured"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[2s] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
              </div>
              
              <div className="absolute inset-0 z-10 p-10 flex flex-col justify-end">
                <span className="text-[10px] font-bold tracking-[0.4em] text-zinc-100 uppercase mb-4">
                  {content.featuredTag}
                </span>
                <div className="flex items-end justify-between">
                  <div className="max-w-md">
                    <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-2">
                      {content.featuredTitle}
                    </h3>
                    <p className="text-zinc-400 text-sm font-light uppercase tracking-widest">
                      {content.featuredSubtitle}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white group-hover:bg-white group-hover:text-black transition-all duration-500">
                    <SafeIcon icon={FiArrowRight} className="text-xl group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Outro Sign-off */}
          <motion.div
            {...fadeInUp}
            className="mt-32 pt-20 border-t border-zinc-900 text-center"
          >
            <p className="text-[10px] font-bold tracking-[0.4em] text-zinc-600 uppercase mb-4">
              Curated in
            </p>
            <p className="text-zinc-100 font-medium tracking-wide">
              Barberà del Vallès, Barcelona
            </p>
          </motion.div>
        </article>
      </main>
    </div>
  );
}